import { extensionWorkOpen, trackExtensionWork } from "./extension-work.ts";
import { hostInputError } from "./host-input.js";
import { McpServerRegistry } from "./mcp-server-registry.ts";
import { STALE_EXTENSION_CONTEXT_MESSAGE } from "./stale-context.ts";
import type {
	Extension,
	ExtensionFlag,
	ExtensionRuntime,
	ExtensionShortcut,
	RegisteredCommand,
	RegisteredTool,
} from "./types.ts";
import { WorkflowActivityHub } from "./workflow-activity-hub.js";
import { WorkflowRunControlHub } from "./workflow-run-control-hub.js";

/** Prepared generations do not own session event delivery until bound by a runner. */
export const boundExtensionRuntimes = new WeakSet<ExtensionRuntime>();

export async function runResourceRegistrationBatch<T>(
	runtime: ExtensionRuntime,
	run: () => Promise<T>,
	completion = false,
): Promise<T> {
	if (!completion && !extensionWorkOpen(runtime)) throw hostInputError("SessionClosed");
	return trackExtensionWork(runtime, async () => {
		if (!runtime.beginResourceRegistrationBatch || !runtime.endResourceRegistrationBatch) return run();
		runtime.beginResourceRegistrationBatch();
		try {
			return await run();
		} finally {
			runtime.endResourceRegistrationBatch();
		}
	});
}

function registrationKey(extension: Extension, name: string): string {
	return `${extension.path}\0${name}`;
}

/** Create a runtime with throwing stubs for action methods. */
export function createExtensionRuntime(): ExtensionRuntime {
	const notInitialized = () => {
		throw new Error("Extension runtime not initialized. Action methods cannot be called during extension loading.");
	};
	const state: { staleMessage?: string } = {};
	const eventBusUnsubscribers = new Set<() => void>();
	let batchDepth = 0;
	const pendingTools = new Map<string, { extension: Extension; name: string; registration: RegisteredTool }>();
	const pendingCommands = new Map<string, { extension: Extension; name: string; registration: RegisteredCommand }>();
	const pendingFlags = new Map<
		string,
		{ extension: Extension; name: string; registration: ExtensionFlag; defaultValue?: boolean | string }
	>();
	const pendingShortcuts = new Map<string, { extension: Extension; name: string; registration: ExtensionShortcut }>();
	const shouldStage = (extension: Extension) =>
		batchDepth > 0 && extension.sourceInfo.configurationOrigin === "inherited-pi";
	let pendingActiveToolNames: string[] | undefined;
	const assertActive = () => {
		if (state.staleMessage) throw new Error(state.staleMessage);
	};

	const runtime: ExtensionRuntime = {
		sendMessage: notInitialized,
		workflowActivityHub: new WorkflowActivityHub((operation) =>
			extensionWorkOpen(runtime) ? trackExtensionWork(runtime, operation) : Promise.resolve(),
		),
		workflowRunControlHub: new WorkflowRunControlHub(),
		mcpServerRegistry: new McpServerRegistry(),
		sendMessages: notInitialized,
		sendUserMessage: notInitialized,
		appendEntry: notInitialized,
		setSessionName: notInitialized,
		getSessionName: notInitialized,
		setLabel: notInitialized,
		getActiveTools: notInitialized,
		getAllTools: notInitialized,
		setActiveTools: notInitialized,
		refreshTools: () => {},
		getCommands: notInitialized,
		setModel: () => Promise.reject(new Error("Extension runtime not initialized")),
		getThinkingLevel: notInitialized,
		setThinkingLevel: notInitialized,
		flagValues: new Map(),
		explicitFlagNames: new Set(),
		flagOwners: new Map(),
		flagOwnerOrigins: new Map(),
		pendingProviderRegistrations: [],
		extensionProviderIds: new Set(),
		canRegisterResource: () => true,
		beginResourceRegistrationBatch: () => {
			batchDepth += 1;
		},
		endResourceRegistrationBatch: () => {
			batchDepth -= 1;
			if (batchDepth !== 0) return;
			const refreshTools = pendingTools.size > 0;
			for (const pending of pendingTools.values()) pending.extension.tools.set(pending.name, pending.registration);
			for (const pending of pendingCommands.values())
				pending.extension.commands.set(pending.name, pending.registration);
			for (const pending of pendingFlags.values()) {
				pending.extension.flags.set(pending.name, pending.registration);
				const ownerOrigin = runtime.flagOwnerOrigins?.get(pending.name);
				if (
					ownerOrigin === pending.extension.sourceInfo.configurationOrigin &&
					pending.defaultValue !== undefined &&
					!runtime.flagValues.has(pending.name)
				) {
					runtime.flagValues.set(pending.name, pending.defaultValue);
				}
			}
			for (const pending of pendingShortcuts.values())
				pending.extension.shortcuts.set(pending.name as never, pending.registration);
			pendingTools.clear();
			pendingCommands.clear();
			pendingFlags.clear();
			pendingShortcuts.clear();
			if (refreshTools) runtime.refreshTools();
			if (pendingActiveToolNames) runtime.setActiveTools(pendingActiveToolNames);
			pendingActiveToolNames = undefined;
		},
		stageToolRegistration: (extension, name, registration) => {
			if (!shouldStage(extension)) return false;
			pendingTools.set(registrationKey(extension, name), { extension, name, registration });
			if (pendingActiveToolNames && !pendingActiveToolNames.includes(name)) pendingActiveToolNames.push(name);
			return true;
		},
		stageCommandRegistration: (extension, name, registration) => {
			if (!shouldStage(extension)) return false;
			pendingCommands.set(registrationKey(extension, name), { extension, name, registration });
			return true;
		},
		stageFlagRegistration: (extension, name, registration, defaultValue) => {
			if (!shouldStage(extension)) return false;
			const key = registrationKey(extension, name);
			const firstDefault = pendingFlags.get(key)?.defaultValue;
			pendingFlags.set(key, { extension, name, registration, defaultValue: firstDefault ?? defaultValue });
			runtime.flagOwners ??= new Map();
			const owners = runtime.flagOwners;
			runtime.flagOwnerOrigins ??= new Map();
			const ownerOrigins = runtime.flagOwnerOrigins;
			if (!owners.has(name)) {
				owners.set(name, extension.path);
				ownerOrigins.set(name, extension.sourceInfo.configurationOrigin);
			}
			return true;
		},
		stageShortcutRegistration: (extension, name, registration) => {
			if (!shouldStage(extension)) return false;
			pendingShortcuts.set(registrationKey(extension, name), { extension, name, registration });
			return true;
		},
		hasPendingResourceRegistration: (extension, resourceType, name) => {
			const key = registrationKey(extension, name);
			if (resourceType === "tool") return pendingTools.has(key);
			if (resourceType === "command") return pendingCommands.has(key);
			if (resourceType === "flag") return pendingFlags.has(key);
			if (resourceType === "shortcut") return pendingShortcuts.has(key);
			return false;
		},
		deletePendingResourceRegistration: (extension, resourceType, name) => {
			const key = registrationKey(extension, name);
			if (resourceType === "tool") pendingTools.delete(key);
			else if (resourceType === "command") pendingCommands.delete(key);
			else if (resourceType === "flag") pendingFlags.delete(key);
			else if (resourceType === "shortcut") pendingShortcuts.delete(key);
		},
		getPendingFlagDefault: (ownerPath, name) => {
			if (!pendingFlags.has(`${ownerPath}\0${name}`)) return undefined;
			return [...pendingFlags.values()].find(
				(pending) => pending.name === name && pending.defaultValue !== undefined,
			)?.defaultValue;
		},
		getAllToolsAfterRegistration: (extension) => {
			const tools = runtime.getAllTools();
			if (extension.sourceInfo.configurationOrigin !== "inherited-pi") return tools;
			const names = new Set(tools.map((tool) => tool.name));
			for (const pending of pendingTools.values()) {
				if (names.has(pending.name)) continue;
				const { definition, sourceInfo } = pending.registration;
				tools.push({
					name: definition.name,
					description: definition.description,
					parameters: definition.parameters,
					...(Object.hasOwn(definition, "constrainedSampling")
						? { constrainedSampling: definition.constrainedSampling }
						: {}),
					promptGuidelines: definition.promptGuidelines,
					exposure: definition.exposure ?? "direct",
					namespace: definition.namespace,
					annotations: definition.annotations,
					sourceInfo,
				});
				names.add(pending.name);
			}
			return tools;
		},
		getCommandsAfterRegistration: (extension) => {
			if (extension.sourceInfo.configurationOrigin !== "inherited-pi") return runtime.getCommands();
			const active = [...pendingCommands.values()].map((pending) => ({
				...pending,
				previous: pending.extension.commands.get(pending.name),
			}));
			for (const pending of active) pending.extension.commands.set(pending.name, pending.registration);
			try {
				return runtime.getCommands();
			} finally {
				for (const pending of active) {
					if (pending.previous) pending.extension.commands.set(pending.name, pending.previous);
					else pending.extension.commands.delete(pending.name);
				}
			}
		},
		refreshToolsAfterRegistration: () => {
			runtime.refreshTools();
			if (batchDepth > 0 && pendingActiveToolNames) pendingActiveToolNames = runtime.getActiveTools();
		},
		applyFlagDefaultAfterRegistration: (name, _ownerPath, value, configurationOrigin) => {
			if (runtime.flagOwnerOrigins?.get(name) === configurationOrigin && !runtime.flagValues.has(name)) {
				runtime.flagValues.set(name, value);
			}
		},
		getActiveToolsAfterRegistration: (extension) => {
			const active = runtime.getActiveTools();
			if (extension.sourceInfo.configurationOrigin !== "inherited-pi") return active;
			if (pendingActiveToolNames) return [...pendingActiveToolNames];
			const names = new Set(active);
			for (const pending of pendingTools.values()) names.add(pending.name);
			return [...names];
		},
		setActiveToolsAfterRegistration: (extension, toolNames) => {
			if (batchDepth === 0) return false;
			pendingActiveToolNames = [...toolNames];
			if (extension.sourceInfo.configurationOrigin !== "inherited-pi") return false;
			const liveNames = new Set(runtime.getAllTools().map((tool) => tool.name));
			runtime.setActiveTools(toolNames.filter((name) => liveNames.has(name)));
			return true;
		},
		assertActive,
		invalidate: (message) => {
			if (state.staleMessage) return;
			state.staleMessage = message ?? STALE_EXTENSION_CONTEXT_MESSAGE;
			const failures: unknown[] = [];
			try {
				runtime.workflowActivityHub.dispose();
			} catch (error) {
				failures.push(error);
			}
			runtime.workflowRunControlHub.dispose();
			for (const unsubscribe of eventBusUnsubscribers) {
				try {
					unsubscribe();
				} catch (error) {
					failures.push(error);
				}
			}
			eventBusUnsubscribers.clear();
			if (failures.length)
				throw Object.assign(new AggregateError(failures, "Extension resource release failed"), {
					code: "ShutdownFailed",
				});
		},
		trackEventBusSubscription: (unsubscribe) => {
			let active = true;
			const trackedUnsubscribe = () => {
				if (!active) return;
				active = false;
				eventBusUnsubscribers.delete(trackedUnsubscribe);
				unsubscribe();
			};
			eventBusUnsubscribers.add(trackedUnsubscribe);
			return trackedUnsubscribe;
		},
		registerProvider: (nameOrProvider, configOrPath, extensionPath = "<unknown>") => {
			if (typeof nameOrProvider === "string") {
				runtime.pendingProviderRegistrations.push({
					name: nameOrProvider,
					config: configOrPath as import("./types.ts").ProviderConfig,
					extensionPath: extensionPath as string,
				});
			} else {
				runtime.pendingProviderRegistrations.push({
					provider: nameOrProvider,
					extensionPath: typeof configOrPath === "string" ? configOrPath : (extensionPath as string),
				});
			}
		},
		unregisterProvider: (name, extensionPath) => {
			runtime.pendingProviderRegistrations = runtime.pendingProviderRegistrations.filter((registration) => {
				const matchesName =
					"provider" in registration ? registration.provider.id === name : registration.name === name;
				if (!matchesName) return true;
				// A transactional rollback owns only what its own extension
				// registered. Without this, a later extension reusing a provider
				// name would drop an earlier extension's working registration.
				return extensionPath !== undefined && registration.extensionPath !== extensionPath;
			});
		},
	};
	return runtime;
}
